import i18n from '@/plugins/i18n'
import map from '@/config/taxonomy-map.json'

// Normaliza para matching robusto: trim + lowercase + sin acentos
function norm(s) {
    return String(s == null ? '' : s).trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
}

function buildLookup(obj) {
    const lookup = {}
    Object.entries(obj || {}).forEach(([name, key]) => {
        if (name.startsWith('_')) return
        lookup[norm(name)] = key
    })
    return lookup
}

const lookups = {
    categories: buildLookup(map.categories),
    objectives: buildLookup(map.objectives),
    sports: buildLookup(map.sports),
    equipments: buildLookup(map.equipments),
    routineCategories: buildLookup(map.routineCategories),
    types: buildLookup(map.types),
    folders: buildLookup(map.folders),
    places: buildLookup(map.places),
    blockNames: buildLookup(map.blockNames),
    countries: buildLookup(map.countries),
    injuryTypes: buildLookup(map.injuryTypes),
    injuryZones: buildLookup(map.injuryZones),
    injuryConditions: buildLookup(map.injuryConditions),
    injuryMoments: buildLookup(map.injuryMoments),
    injuryComplexity: buildLookup(map.injuryComplexity),
}

// Resuelve un valor (nombre ES legacy O key) a su key estable, o null si no esta mapeado.
function resolveKey(group, value) {
    const lk = lookups[group] || {}
    const n = norm(value)
    if (lk[n]) return lk[n]
    if (Object.values(lk).includes(String(value))) return String(value)
    return null
}

// Traduce via i18n por key; si no hay key/clave, devuelve el valor crudo (fallback).
function label(group, value) {
    const key = resolveKey(group, value)
    if (!key) return value == null ? '' : value
    const path = `${group}.${key}`
    const translated = i18n.t(path)
    return translated === path ? (value == null ? '' : value) : translated
}

// Keys canonicas de categoria (para comparar en logica de negocio sin depender del idioma)
export const CATEGORY = {
    STRONG: 'strong',
    SPEED: 'speed',
    RESISTANCE: 'resistance',
    STABILITY: 'stability',
    MOBILITY: 'mobility',
    EVALUATION: 'evaluation',
    METABOLIC: 'metabolic',
    MIX: 'mix',
}

export function categoryKey(value) { return resolveKey('categories', value) }
export function categoryLabel(value) { return label('categories', value) }

export function objectiveKey(value) { return resolveKey('objectives', value) }
export function objectiveLabel(value) { return label('objectives', value) }

export function sportKey(value) { return resolveKey('sports', value) }
export function sportLabel(value) { return label('sports', value) }

export function equipmentKey(value) { return resolveKey('equipments', value) }
export function equipmentLabel(value) { return label('equipments', value) }

export function typeKey(value) { return resolveKey('types', value) }
export function typeLabel(value) { return label('types', value) }

// Carpetas: solo las globales (sin coachId) se traducen; las del coach quedan como las escribio.
export function folderLabel(folder) {
    if (!folder) return ''
    if (folder.coachId) return folder.name == null ? '' : folder.name
    return label('folders', folder.name)
}

export function placeKey(value) { return resolveKey('places', value) }
export function placeLabel(value) { return label('places', value) }

// Nombres sugeridos de bloque: el coach puede escribir el suyo, por eso el fallback al valor crudo importa.
export function blockNameLabel(value) { return label('blockNames', value) }

export function countryKey(value) { return resolveKey('countries', value) }
export function countryLabel(value) { return label('countries', value) }

// Keys canonicas de complejidad de lesion (para la logica de severidad sin depender del idioma)
export const INJURY_COMPLEXITY = {
    VERY_LOW: 'very_low',
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    VERY_HIGH: 'very_high',
}

export function injuryTypeKey(value) { return resolveKey('injuryTypes', value) }
export function injuryTypeLabel(value) { return label('injuryTypes', value) }

export function injuryZoneKey(value) { return resolveKey('injuryZones', value) }
export function injuryZoneLabel(value) { return label('injuryZones', value) }

export function injuryConditionKey(value) { return resolveKey('injuryConditions', value) }
export function injuryConditionLabel(value) { return label('injuryConditions', value) }

export function injuryMomentKey(value) { return resolveKey('injuryMoments', value) }
export function injuryMomentLabel(value) { return label('injuryMoments', value) }

export function injuryComplexityKey(value) { return resolveKey('injuryComplexity', value) }
export function injuryComplexityLabel(value) { return label('injuryComplexity', value) }

// Objetivo de rutina: comparte vocabulario y namespace i18n con categories.
export function routineObjectiveKey(value) { return resolveKey('routineCategories', value) }
export function routineObjectiveLabel(value) {
    const key = resolveKey('routineCategories', value)
    return label('categories', key || value)
}
